import { Dashboardv20240509Api } from '@sequencehq/api/dashboard/v20240509'
import { IntegrationServices } from '@sequencehq/api/commonEnums'
import { IntegrationsFeatureFlags } from 'Integrations/domain/integrations.domain'
import { getCachedIntegrations } from 'Integrations/utils/getCachedIntegrations'
import { INTEGRATIONS_CONFIG } from 'modules/Integrations/integrationsConfig/integrations.config'

export type v1ApiLoadedData = {
  integrations: Array<
    Dashboardv20240509Api.GetIntegrations.Integration & {
      configuration: Record<string, unknown>
    }
  >
  featuredIntegrations: Array<IntegrationServices>
}

/**
 * A collection of integrations that do not exist on the service (or in general
 * yet). These are layered onto the response from the API, but should be cleaned
 * away should these be added to the service proper.
 */
const COMING_SOON_INTEGRATIONS: v1ApiLoadedData['integrations'] = [
  {
    service: 'Anrok',
    //@ts-expect-error this also isn't a real type (for now)
    type: 'Tax',
    state: 'COMING_SOON',
    enabled: false,
    configuration: {}
  },
  {
    //@ts-expect-error this integration isn't real (for now)
    service: 'Dropbox_Sign',
    //@ts-expect-error this integration type isn't real (for now)
    type: 'E-signatures',
    state: 'AVAILABLE',
    enabled: true,
    configuration: {}
  },
  {
    //@ts-expect-error this integration isn't real (for now)
    service: 'Equals',
    type: 'Reporting',
    state: 'COMING_SOON',
    enabled: false,
    configuration: {}
  },
  {
    //@ts-expect-error this integration isn't real (for now)
    service: 'Zapier',
    type: 'Workflows',
    state: 'AVAILABLE',
    enabled: false,
    configuration: {}
  },
  {
    service: 'Numeral',
    //@ts-expect-error this integration isn't real (for now)
    type: 'Tax',
    state: 'COMING_SOON',
    enabled: false,
    configuration: {}
  },
  {
    service: 'Sphere',
    //@ts-expect-error this integration type isn't real (for now)
    type: 'Tax',
    state: 'COMING_SOON',
    enabled: false,
    configuration: {}
  }
]

export const loadData = async ({
  flags
}: {
  flags: IntegrationsFeatureFlags
}): Promise<v1ApiLoadedData> => {
  /**
   * `loadData` is the integrations module's entry point and is also called
   * from `reloadIntegrations` after events that change integration state
   * (post-OAuth polling sees CONNECTED, configuration save, manual refresh).
   * Pass `reload: true` so the read bypasses the cached entry and refetches
   * from the network. Other consumers (`useFetchIntegrations`, banners) still
   * benefit from the populated cache afterwards.
   */
  let response
  try {
    response = await getCachedIntegrations({ reload: true })
  } catch {
    throw new Error('Failed to load integrations')
  }

  const integrations = response?.items ?? []

  const comingSoonFiltered = COMING_SOON_INTEGRATIONS.filter(
    comingSoon => !integrations.some(int => int.service === comingSoon.service)
  )

  const resolvedIntegrations = await Promise.all(
    [...comingSoonFiltered, ...integrations].map(async int => {
      // Gate the backend NetSuite value to "Coming soon" until the flag is on.
      if (int.service === 'NetSuite' && !flags.useNetsuite) {
        return {
          ...int,
          state: 'COMING_SOON' as const,
          enabled: false,
          configuration: {}
        }
      }

      if (flags.showAttioIntegration && int.service === 'Attio') {
        return {
          ...int,
          state: 'CONNECTED' as const,
          configuration: {}
        }
      }

      const config = INTEGRATIONS_CONFIG[int.service]
      if (!config?.communication?.configuration?.loader) {
        return {
          ...int,
          configuration: {}
        }
      }

      const loaderData =
        (await config.communication.configuration.loader())?.data ?? {}

      const resolved = {
        ...int,
        configuration: loaderData
      }

      if (
        int.service === 'Anrok' &&
        loaderData.obfuscatedApiKey &&
        loaderData.obfuscatedApiKey !== ''
      ) {
        return {
          ...resolved,
          enabled: true,
          state: 'CONNECTED' as const
        }
      }

      return resolved
    })
  )

  return {
    integrations: resolvedIntegrations,
    featuredIntegrations: ['Xero', 'Stripe', 'HubSpot']
  }
}
